/**
 * Help-centre translation via Google's free website translator: no Cloud
 * Translation API, no key. The public translate element rewrites visible text
 * in place; the chosen language is kept in Google's `googtrans` cookie, so
 * every page (and each client-side navigation) stays translated.
 *
 * Same approach as the welcome guide in noe-app
 * (src/components/welcome-guide-public/guideTranslate.ts), trimmed for a
 * stateless docs site: going back to French simply reloads the page.
 *
 * Zudoku renders with React, and Google wraps text nodes in its own <font>
 * tags; React then throws on removeChild/insertBefore during navigation. The
 * prototype patch below is the usual fix for that clash.
 */

export const PAGE_LANGUAGE = 'fr';

export const LANGUAGES = [
  { code: 'fr', label: 'Français', flag: '🇫🇷' },
  { code: 'en', label: 'English', flag: '🇬🇧' },
  { code: 'es', label: 'Español', flag: '🇪🇸' },
  { code: 'it', label: 'Italiano', flag: '🇮🇹' },
  { code: 'de', label: 'Deutsch', flag: '🇩🇪' },
  { code: 'pt', label: 'Português', flag: '🇵🇹' },
  { code: 'nl', label: 'Nederlands', flag: '🇳🇱' },
  { code: 'ar', label: 'العربية', flag: '🇸🇦' },
] as const;

export type LanguageCode = (typeof LANGUAGES)[number]['code'];

const STORAGE_KEY = 'noe.docs.lang';
const HOST_ID = 'noe-google-translate';
const SCRIPT_ID = 'noe-google-translate-script';
const STYLE_ID = 'noe-translate-overrides';

const OVERRIDE_CSS = `
  body { top: 0 !important; }
  iframe.skiptranslate,
  .goog-te-banner-frame,
  #goog-gt-tt,
  .goog-te-balloon-frame,
  body > .skiptranslate:not(#${HOST_ID}) {
    display: none !important;
  }
  html.translated-ltr font,
  html.translated-rtl font {
    background-color: transparent !important;
    box-shadow: none !important;
  }
  #${HOST_ID} {
    position: fixed !important;
    left: -9999px !important;
    top: 0 !important;
    width: 1px !important;
    height: 1px !important;
    overflow: hidden !important;
    clip: rect(0, 0, 0, 0) !important;
    pointer-events: none !important;
  }
`;

interface TranslateElementCtor {
  new (options: Record<string, unknown>, elementId: string): unknown;
}

declare global {
  interface Window {
    google?: { translate?: { TranslateElement: TranslateElementCtor } };
    googleTranslateElementInit?: () => void;
  }
}

export function isLanguage(code: string): code is LanguageCode {
  return LANGUAGES.some((language) => language.code === code);
}

/** The language in force: Google's cookie first (it is what translates), then the last choice. */
export function currentLanguage(): LanguageCode {
  const cookie = /(?:^|;\s*)googtrans=\/[^/;]*\/([^;]+)/.exec(document.cookie)?.[1];
  if (cookie && isLanguage(cookie)) return cookie;
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved && isLanguage(saved)) return saved;
  } catch {
    /* private mode */
  }
  return PAGE_LANGUAGE;
}

/** Host-only cookie when setting; clearing also drops the parent-domain copies Google writes. */
function writeGoogTrans(value: string | null) {
  const expires = value ? '' : ';expires=Thu, 01 Jan 1970 00:00:00 GMT';
  const payload = value ? `googtrans=${value}` : 'googtrans=';
  document.cookie = `${payload}${expires};path=/`;
  if (value) return;
  const parts = location.hostname.split('.');
  for (let i = 0; i < parts.length - 1; i += 1) {
    const domain = parts.slice(i).join('.');
    document.cookie = `${payload}${expires};path=/;domain=${domain}`;
    document.cookie = `${payload}${expires};path=/;domain=.${domain}`;
  }
}

function installDomPatch() {
  const proto = Node.prototype as Node & { __noeTranslatePatched?: boolean };
  if (proto.__noeTranslatePatched) return;
  proto.__noeTranslatePatched = true;
  const removeChild = proto.removeChild;
  const insertBefore = proto.insertBefore;
  proto.removeChild = function removeChildPatched<T extends Node>(this: Node, child: T): T {
    if (child.parentNode !== this) return child;
    return removeChild.call(this, child) as T;
  };
  proto.insertBefore = function insertBeforePatched<T extends Node>(this: Node, node: T, ref: Node | null): T {
    if (ref && ref.parentNode !== this) return node;
    return insertBefore.call(this, node, ref) as T;
  };
}

function ensureChrome() {
  if (!document.getElementById(STYLE_ID)) {
    const style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent = OVERRIDE_CSS;
    document.head.appendChild(style);
  }
  if (!document.getElementById(HOST_ID)) {
    const host = document.createElement('div');
    host.id = HOST_ID;
    host.className = 'notranslate';
    host.setAttribute('translate', 'no');
    host.setAttribute('aria-hidden', 'true');
    document.body.appendChild(host);
  }
}

let scriptPromise: Promise<void> | null = null;

function ensureScript(): Promise<void> {
  if (scriptPromise) return scriptPromise;
  scriptPromise = new Promise<void>((resolve, reject) => {
    window.googleTranslateElementInit = () => {
      const ctor = window.google?.translate?.TranslateElement;
      const host = document.getElementById(HOST_ID);
      if (ctor && host && host.dataset.ready !== '1') {
        host.dataset.ready = '1';
        new ctor(
          {
            pageLanguage: PAGE_LANGUAGE,
            includedLanguages: LANGUAGES.filter((l) => l.code !== PAGE_LANGUAGE).map((l) => l.code).join(','),
            autoDisplay: false,
          },
          HOST_ID,
        );
      }
      resolve();
    };
    if (document.getElementById(SCRIPT_ID)) return;
    const script = document.createElement('script');
    script.id = SCRIPT_ID;
    script.src = 'https://translate.google.com/translate_a/element.js?cb=googleTranslateElementInit';
    script.async = true;
    script.onerror = () => {
      scriptPromise = null;
      reject(new Error('translate script failed'));
    };
    document.body.appendChild(script);
  });
  return scriptPromise;
}

function waitForCombo(timeoutMs = 4000): Promise<HTMLSelectElement | null> {
  return new Promise((resolve) => {
    const started = Date.now();
    const tick = () => {
      const select = document.querySelector<HTMLSelectElement>('select.goog-te-combo');
      if (select && select.options.length > 1) return resolve(select);
      if (Date.now() - started > timeoutMs) return resolve(null);
      window.setTimeout(tick, 50);
    };
    tick();
  });
}

async function selectCombo(code: LanguageCode): Promise<boolean> {
  const select = await waitForCombo();
  if (!select || !Array.from(select.options).some((o) => o.value === code)) return false;
  select.value = code;
  select.dispatchEvent(new Event('change', { bubbles: true }));
  return true;
}

/**
 * While a page is translated, open internal links as full page loads. Zudoku
 * swaps pages client-side, and React updating text that Google has rewritten
 * leaves the previous page's translated text on screen (seen 2026-10-16). A
 * fresh load hydrates the French page, then translates it.
 */
function onLinkClick(event: MouseEvent) {
  if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
  const link = (event.target as Element | null)?.closest?.('a[href]') as HTMLAnchorElement | null;
  if (!link || link.target === '_blank' || link.hasAttribute('download')) return;
  const url = new URL(link.href, location.href);
  if (url.origin !== location.origin) return;
  if (url.pathname === location.pathname && url.hash) return; // in-page anchor
  event.preventDefault();
  event.stopPropagation();
  location.assign(url.href);
}

let fullPageLinks = false;
function useFullPageLinks() {
  if (fullPageLinks) return;
  fullPageLinks = true;
  document.addEventListener('click', onLinkClick, true);
}

/**
 * Call once in the browser, after hydration. When a language other than
 * French is saved, translate this page and keep links as full page loads.
 */
export function mountTranslate() {
  installDomPatch();
  ensureChrome();
  const lang = currentLanguage();
  if (lang === PAGE_LANGUAGE) return;
  writeGoogTrans(`/${PAGE_LANGUAGE}/${lang}`);
  useFullPageLinks();
  // Let React finish hydrating every lazy part of the page first: Google
  // rewriting text before that makes React throw away and re-render the tree.
  const start = () =>
    void ensureScript()
      .then(() => selectCombo(lang))
      .catch(() => undefined);
  const idle = (window as Window & { requestIdleCallback?: (cb: () => void, o?: { timeout: number }) => void })
    .requestIdleCallback;
  const later = () => (idle ? idle(start, { timeout: 1500 }) : window.setTimeout(start, 600));
  if (document.readyState === 'complete') later();
  else window.addEventListener('load', later, { once: true });
}

/** Switch language. French removes the translation (a reload: the docs hold no state). */
export async function applyLanguage(code: LanguageCode): Promise<void> {
  try {
    localStorage.setItem(STORAGE_KEY, code);
  } catch {
    /* private mode */
  }
  if (code === PAGE_LANGUAGE) {
    writeGoogTrans(null);
    window.location.reload();
    return;
  }
  writeGoogTrans(`/${PAGE_LANGUAGE}/${code}`);
  useFullPageLinks();
  try {
    await ensureScript();
    if (await selectCombo(code)) return;
  } catch {
    /* fall through: the cookie translates the page on reload */
  }
  window.location.reload();
}
